/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { ColDef, GridReadyEvent, IServerSideDatasource } from '@ag-grid-community/core';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import * as _ from 'lodash';

import { FilterQueryOp } from 'breeze-client';
import { DomainBaseComponent } from '@/shared/domain-base.component';
import { Feature, FeatureChoice, Product, ProductTypeTag } from '@/model/entities/entity-model';
import { DomainService } from '@/shared/domain.service';
import { DialogService } from 'primeng/dynamicdialog';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '@/shared/auth.service';
import { takeUntil } from 'rxjs';
import { AgFns, GridOptionsExt, ISortModel } from '@/shared/ag-fns';
import { NavFns } from '@/shared/nav-fns';
import { UtilFns } from '@/shared/util-fns';
import { FieldLike, ProductsWhere } from '@/shared/db-query.service';
import { ProductFns } from './product-fns';
import { DialogBaseComponent } from '@/shared/dialog-base.component';
import { EditProductDialogComponent } from './edit-product-dialog.component';
import { UniAgFns } from '@/shared/uni-ag-fns';

@Component({
  selector: 'app-product-search',
  templateUrl: './product-search.component.html',
  standalone: false,
})
export class ProductSearchComponent extends DomainBaseComponent implements OnInit {
  manufacturerName = '';
  manufacturerOp = FilterQueryOp.StartsWith;
  productTypeStyle = '';
  productTypeStyleOp = FilterQueryOp.StartsWith;
  productTypeDescription = '';
  productTypeDescriptionOp = FilterQueryOp.StartsWith;
  productTypeDescriptionExt = '';
  productTypeDescriptionExtOp = FilterQueryOp.Contains;
  sku = '';
  skuOp = FilterQueryOp.Contains;
  productTypeTagsAndOr: 'and' | 'or' = 'and';
  featuresAndOr: 'and' | 'or' = 'and';

  filterQueryOps = [FilterQueryOp.Equals, FilterQueryOp.StartsWith, FilterQueryOp.Contains, FilterQueryOp.EndsWith];

  allProductTypeTags: ProductTypeTag[] = [];
  selectedProductTypeTags: ProductTypeTag[] = [];

  allFeatures: Feature[] = [];
  searchFeatures: Feature[] = [];
  selectedFeature?: Feature;
  altSelectedFeature?: Feature;

  featureChoices: FeatureChoice[] = [];
  selectedFeatureChoices: FeatureChoice[] = [];

  // key is feature
  savedFeatureChoiceMap = new Map<Feature, FeatureChoice[]>();

  featureSet = new Set<Feature>();

  productWhere?: ProductsWhere;
  productGridOptions: GridOptionsExt = {};
  editLabel = 'Edit';

  shouldShowAllFeatures = true;
  maxFeatureChoices = 50;

  datasource?: IServerSideDatasource;

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    protected authService: AuthService,
    public pngDialogService: DialogService,
    private http: HttpClient,
  ) {
    super(domainService);

    this.route.paramMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });
  }

  async updateFromContext() {
    [this.allFeatures, this.allProductTypeTags] = await this.dbQueryService.getAllFeatureRelatedInfo();
    this.allFeatures = _.sortBy(this.allFeatures, (t) => t.name);

    this.productGridOptions = this.AgFns.initGridOptions(this, {
      onGridReady: this.onProductGridReady,

      rowModelType: 'serverSide',
    });

    this.onWhereChange();

    this.isPageReady = true;
  }

  onProductGridReady(event: GridReadyEvent) {
    const [colDefs, sortModel] = this.getProductColDefsAndSortModel();
    this.AgFns.initGrid(event, colDefs, sortModel);

    if (this.datasource) {
      this.productGridOptions.apiX?.setGridOption('serverSideDatasource', this.datasource);
    }
  }

  getProductColDefsAndSortModel() {
    let featureColDefs: ColDef[] = [];
    if (this.shouldShowAllFeatures) {
      featureColDefs = [ProductFns.getAllFeaturesColDef()];
    } else if (!_.isEmpty(this.savedFeatureChoiceMap)) {
      this.featureSet = new Set(Array.from(this.savedFeatureChoiceMap.keys()));
      featureColDefs = ProductFns.getFeatureColDefs(Array.from(this.featureSet));
    }

    const colDefs: ColDef[] = [
      // { headerName: 'Id', field: 'id'  },
      {
        ...AgFns.createButtonProps('', this.goEditProduct.bind(this), {
          label: this.editLabel,
        }),
        ...AgFns.fixWidth(80),
      },
      { ...UniAgFns.getManufacturerColDef('productType.manufacturer.name') },
      {
        headerName: 'SKU',
        field: 'productType.style',
        ...NavFns.createCellClickedCalcNavProps(this.router, (event) => {
          const prod = event.data as Product;
          return { navPath: `/references/manage/product-type/${prod.productTypeId}` };
        }),
      },

      // Generated colDefs here
      ...featureColDefs,

      { ...UniAgFns.getProductTypeDescriptionColDef('productType.description') },
      // ProxAgFns.getWorkingStatusDef('Product Type Status', this.statusService, p => p.data.productType),
      // ProxAgFns.getWorkingStatusDef('Product Status', this.statusService),
    ];

    const sortModel = <ISortModel>[
      { colId: 'productType.manufacturer.name', sort: 'asc' },
      { colId: 'productType.name', sort: 'asc' },
    ];
    return [colDefs, sortModel] as const;
  }

  async onSelectedFeatureChange() {
    if (this.selectedFeature == null) {
      this.selectedFeatureChoices = [];
      this.featureChoices = [];
      return;
    }
    const isNewSearchFeature = !_.includes(this.searchFeatures, this.selectedFeature);
    if (isNewSearchFeature) {
      this.searchFeatures.push(this.selectedFeature);
      this.saveFeatureChoices(this.selectedFeature, []);
      if (!this.shouldShowAllFeatures) this.recalcColDefs();
    }
    this.featureChoices = this.getFeatureChoices(this.selectedFeature);

    this.featureChoices = _.sortBy(
      this.featureChoices,
      (fc) => fc.displayOrder,
      (fc) => fc.choiceValue,
    );
    this.selectedFeatureChoices = this.getSavedFeatureChoices(this.selectedFeature);
    if (isNewSearchFeature) {
      this.onWhereChange();
    }
  }

  onShowFeaturesChange() {
    this.recalcColDefs();
  }

  async onDeleteFeature(feature: Feature) {
    if (feature == null) return;
    this.savedFeatureChoiceMap.delete(feature);
    if (!this.shouldShowAllFeatures) this.recalcColDefs();
    _.remove(this.searchFeatures, feature);
    this.selectedFeature = _.first(Array.from(this.savedFeatureChoiceMap.keys()));
    this.onSelectedFeatureChange();
    this.onWhereChange();
  }

  getFeatureChoices(feature: Feature) {
    const featureChoices = feature.featureChoices;
    return featureChoices;
  }

  async onFeatureClick() {
    // wait below is important because selectedFeature does not change immediately
    await UtilFns.wait(100);
    this.onSelectedFeatureChange();
  }

  onCriteriaClick(feature: Feature) {
    this.selectedFeature = feature;
    this.onSelectedFeatureChange();
  }

  async onFeatureChoiceChange() {
    if (this.selectedFeatureChoices == null) {
      return;
    }
    if (this.selectedFeature == null) {
      return;
    }
    this.saveFeatureChoices(this.selectedFeature, this.selectedFeatureChoices);
    this.onWhereChange();
  }

  formatSelectedChoices(feature: Feature) {
    const selectedFeatureChoices = this.getSavedFeatureChoices(feature);
    if (selectedFeatureChoices.length === 0) return '';
    const maxDisplay = 60;
    const names = selectedFeatureChoices.map((fc) => fc.choiceValue);
    if (names.length <= maxDisplay) return names.join(', ');
    const remaining = names.length - maxDisplay;
    return names.slice(0, maxDisplay).join(', ') + ` and ${remaining} more...`;
  }

  onDeleteFeatureSearch(_feature: Feature, event: Event) {
    // TODO: fix this code to get the right behavior

    this.onWhereChange();
    event.stopPropagation();
  }

  saveFeatureChoices(feature: Feature, featureChoices: FeatureChoice[]) {
    this.savedFeatureChoiceMap.set(feature, featureChoices);
  }

  getSavedFeatureChoices(feature: Feature) {
    return this.savedFeatureChoiceMap.get(feature) ?? [];
  }

  async onWhereChange() {
    const where = this.productWhere || <ProductsWhere>{};

    where.manufacturerName = FieldLike.create(this.manufacturerName, this.manufacturerOp);
    where.productTypeStyle = FieldLike.create(this.productTypeStyle, this.productTypeStyleOp);
    where.productTypeDescription = FieldLike.create(this.productTypeDescription, this.productTypeDescriptionOp);
    where.productTypeDescriptionExt = FieldLike.create(this.productTypeDescriptionExt, this.productTypeDescriptionExtOp);

    where.productTypeTags = this.selectedProductTypeTags;
    where.productTypeTagsAndOr = this.productTypeTagsAndOr;
    where.featuresAndOr = this.featuresAndOr;

    const fcMap = Array.from(this.savedFeatureChoiceMap.entries()).map(([f, fcs]) => {
      return { feature: f, featureChoices: fcs };
    });
    where.featureChoices = _.flatten(fcMap.map((x) => x.featureChoices));
    where.features = fcMap.map((x) => x.feature);

    this.datasource = AgFns.buildDatasource(() => this.dbQueryService.createProductsWhereQuery(where), 'id', {
      returnFn: (r) => this.checkFeatureColDefs(r),
    });

    if (this.productGridOptions.apiX) {
      this.productGridOptions.apiX.setGridOption('serverSideDatasource', this.datasource);
    }
  }

  checkFeatureColDefs(products: Product[]) {
    if (!this.shouldShowAllFeatures) return;
    const fcs = _.flatMap(products, (p) => p.productFeatureChoices.map((pfc) => pfc.featureChoice));
    const newFeatureSet = new Set(fcs.map((x) => x.feature));
    // same as newFeatureSet.difference(this.featureSet) -- not yet available on all browsers.
    const hasDiff = [...newFeatureSet].some((x) => !this.featureSet.has(x));

    if (hasDiff) {
      this.featureSet = new Set([...newFeatureSet, ...this.featureSet]);
      this.recalcColDefs();
    }
  }

  private recalcColDefs() {
    if (this.productGridOptions.apiX == null) {
      return;
    }
    const [colDefs, sortModel] = this.getProductColDefsAndSortModel();
    this.productGridOptions.apiX.setGridOption('columnDefs', colDefs);
    this.productGridOptions.apiX?.applyColumnState({ state: sortModel });
  }

  async goEditProduct(product: Product) {
    const r = await DialogBaseComponent.open(EditProductDialogComponent, this, {
      product: product,
    });
    if (r) {
      // await this.refreshProducts();
    }
    return r;
  }
}
